import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	BarChart3,
	TrendingUp,
	Building2,
	Users,
	Crown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";

// Fetch functions
async function fetchOverview() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/analytics/overview"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchGrowth(period: string) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/analytics/growth?period=${period}`), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchUsage() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/analytics/usage"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

const planColors: Record<string, string> = {
	free: "bg-gray-500",
	starter: "bg-blue-500",
	professional: "bg-purple-500",
	enterprise: "bg-amber-500",
};

const periods = [
	{ value: "7d", label: "7 days" },
	{ value: "30d", label: "30 days" },
	{ value: "90d", label: "90 days" },
	{ value: "365d", label: "1 year" },
];

export function AnalyticsDashboard() {
	const [selectedPeriod, setSelectedPeriod] = useState("30d");

	const { data: overview } = useQuery({
		queryKey: ["analytics-overview"],
		queryFn: fetchOverview,
	});

	const { data: growth } = useQuery({
		queryKey: ["analytics-growth", selectedPeriod],
		queryFn: () => fetchGrowth(selectedPeriod),
	});

	const { data: usage } = useQuery({
		queryKey: ["analytics-usage"],
		queryFn: fetchUsage,
	});

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
					<p className="text-gray-500 mt-1">
						Platform analytics and growth metrics
					</p>
				</div>
			</div>

			{/* Summary Cards */}
			{overview && (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-100 rounded-lg">
									<Building2 className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overview.summary.totalOrganizations}
									</p>
									<p className="text-sm text-gray-500">Organizations</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-green-100 rounded-lg">
									<Crown className="w-5 h-5 text-green-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overview.summary.activeOrganizations}
									</p>
									<p className="text-sm text-gray-500">Active Orgs</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-purple-100 rounded-lg">
									<Users className="w-5 h-5 text-purple-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overview.summary.totalUsers}
									</p>
									<p className="text-sm text-gray-500">Total Users</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-amber-100 rounded-lg">
									<TrendingUp className="w-5 h-5 text-amber-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overview.summary.conversionRate}%
									</p>
									<p className="text-sm text-gray-500">Conversion Rate</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Plan Distribution */}
				<Card>
					<CardHeader>
						<CardTitle>Plan Distribution</CardTitle>
					</CardHeader>
					<CardContent>
						{overview?.planDistribution ? (
							<div className="space-y-4">
								{Object.entries(overview.planDistribution).map(
									([plan, count]: [string, any]) => {
										const total = Object.values(
											overview.planDistribution,
										).reduce((a: number, b: any) => a + b, 0);
										const percentage = Math.round((count / total) * 100);
										return (
											<div key={plan}>
												<div className="flex items-center justify-between mb-1">
													<span className="text-sm font-medium capitalize">
														{plan}
													</span>
													<span className="text-sm text-gray-500">
														{count} ({percentage}%)
													</span>
												</div>
												<div className="w-full bg-gray-200 rounded-full h-2.5">
													<div
														className={cn(
															"h-2.5 rounded-full",
															planColors[plan] || "bg-gray-500",
														)}
														style={{ width: `${percentage}%` }}
													/>
												</div>
											</div>
										);
									},
								)}
							</div>
						) : (
							<p className="text-gray-500 text-center py-4">No data available</p>
						)}
					</CardContent>
				</Card>

				{/* Status Distribution */}
				<Card>
					<CardHeader>
						<CardTitle>Subscription Status</CardTitle>
					</CardHeader>
					<CardContent>
						{overview?.statusDistribution ? (
							<div className="space-y-3">
								{Object.entries(overview.statusDistribution).map(
									([status, count]: [string, any]) => (
										<div
											key={status}
											className="flex items-center justify-between py-2 border-b last:border-0"
										>
											<span className="capitalize">
												{status.replace(/_/g, " ")}
											</span>
											<Badge className="bg-gray-100 text-gray-700">{count}</Badge>
										</div>
									),
								)}
							</div>
						) : (
							<p className="text-gray-500 text-center py-4">No data available</p>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Growth Chart */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span className="flex items-center gap-2">
							<BarChart3 className="w-5 h-5" />
							Growth Over Time
						</span>
						<div className="flex gap-2">
							{periods.map((period) => (
								<Button
									key={period.value}
									variant={selectedPeriod === period.value ? "default" : "outline"}
									size="sm"
									onClick={() => setSelectedPeriod(period.value)}
								>
									{period.label}
								</Button>
							))}
						</div>
					</CardTitle>
				</CardHeader>
				<CardContent>
					{growth ? (
						<div className="space-y-6">
							<div className="grid grid-cols-2 gap-4">
								<div className="p-4 bg-blue-50 rounded-lg">
									<p className="text-sm text-blue-600 font-medium">
										New Organizations
									</p>
									<p className="text-3xl font-bold text-blue-700">
										{growth.summary.newOrganizations}
									</p>
								</div>
								<div className="p-4 bg-purple-50 rounded-lg">
									<p className="text-sm text-purple-600 font-medium">New Users</p>
									<p className="text-3xl font-bold text-purple-700">
										{growth.summary.newUsers}
									</p>
								</div>
							</div>

							{/* Simple text-based timeline */}
							<div className="space-y-2">
								<h4 className="font-medium text-gray-700">Organization Growth</h4>
								<div className="max-h-40 overflow-y-auto space-y-1">
									{growth.organizationGrowth.slice(-10).map((item: any) => (
										<div
											key={item.date}
											className="flex items-center justify-between text-sm"
										>
											<span className="text-gray-500">{item.date}</span>
											<div className="flex items-center gap-2">
												<div
													className="h-2 bg-blue-500 rounded"
													style={{
														width: `${Math.max(item.count * 10, 4)}px`,
													}}
												/>
												<span className="font-medium">{item.count}</span>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					) : (
						<p className="text-gray-500 text-center py-4">Loading...</p>
					)}
				</CardContent>
			</Card>

			{/* Top Organizations */}
			{usage?.topOrganizations && usage.topOrganizations.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Crown className="w-5 h-5" />
							Top Organizations by Users
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="overflow-x-auto">
							<table className="w-full">
								<thead>
									<tr className="border-b">
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Organization
										</th>
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Plan
										</th>
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Status
										</th>
										<th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">
											Users
										</th>
									</tr>
								</thead>
								<tbody>
									{usage.topOrganizations.map((org: any, index: number) => (
										<tr
											key={org._id}
											className="border-b last:border-0 hover:bg-gray-50"
										>
											<td className="py-3 px-4">
												<div className="flex items-center gap-2">
													<span className="text-gray-400 font-medium">
														#{index + 1}
													</span>
													<span className="font-medium">{org.name}</span>
												</div>
											</td>
											<td className="py-3 px-4">
												<Badge
													className={cn(
														"text-white",
														planColors[org.plan] || "bg-gray-500",
													)}
												>
													{org.plan}
												</Badge>
											</td>
											<td className="py-3 px-4">
												<Badge
													className={
														org.status === "active"
															? "bg-green-100 text-green-700"
															: "bg-gray-100 text-gray-700"
													}
												>
													{org.status || "inactive"}
												</Badge>
											</td>
											<td className="py-3 px-4 text-right font-medium">
												{org.userCount}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
